import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Brush, Code, Eye, FileText, Indent, Loader2, PenLine, RefreshCw } from "lucide-react";
import {
  cleanPastedStyles,
  formatHtml,
  htmlToPlainText,
  isFullHtmlDocument,
  isHtmlEmpty,
} from "../lib/htmlToText";
import { cn } from "../lib/utils";

// CKEditor is ~470 KB gzip; keep it out of the main bundle so it only
// downloads when the Design tab actually mounts (Creatives upload / edit).
const RichTextEditor = lazy(() => import("./RichTextEditor"));

type ContentTab = "design" | "preview" | "html" | "text";

const TABS: { id: ContentTab; label: string; icon: typeof Code }[] = [
  { id: "design", label: "Design", icon: PenLine },
  { id: "preview", label: "Preview", icon: Eye },
  { id: "html", label: "HTML", icon: Code },
  { id: "text", label: "Text", icon: FileText },
];

interface EmailContentEditorProps {
  html: string;
  onHtmlChange: (html: string) => void;
  text: string;
  onTextChange: (text: string) => void;
  /** Text the user has edited by hand, so regenerating warns first */
  minHeight?: string;
  className?: string;
}

export default function EmailContentEditor({
  html,
  onHtmlChange,
  text,
  onTextChange,
  minHeight = "26rem",
  className,
}: EmailContentEditorProps) {
  // A full email document opens in Preview -- Design is a lossy view of
  // table-based email markup, not the source of truth
  const [activeTab, setActiveTab] = useState<ContentTab>(() =>
    isFullHtmlDocument(html) ? "preview" : "design"
  );
  // Remembers what we last generated, so we can tell "untouched" from "edited"
  const [lastGeneratedText, setLastGeneratedText] = useState<string | null>(null);

  // What we last sent up, so HTML arriving from outside (a file being loaded)
  // is distinguishable from our own edit coming back
  const lastEmittedRef = useRef(html);

  const handleHtmlChange = (next: string) => {
    lastEmittedRef.current = next;
    onHtmlChange(next);
  };

  useEffect(() => {
    if (html === lastEmittedRef.current) return;
    lastEmittedRef.current = html;
    // Loaded from a file: show it as it really renders, not as Design flattens it
    if (isFullHtmlDocument(html)) setActiveTab("preview");
  }, [html]);

  // Parses the markup, so don't redo it on every keystroke in the HTML tab
  const htmlEmpty = useMemo(() => isHtmlEmpty(html), [html]);
  // getData() returns one long line; indent it for the source view so the
  // user isn't reading a wall of markup
  const displayedHtml = html.includes("\n") ? html : formatHtml(html);
  const textEditedByHand =
    text.trim().length > 0 && lastGeneratedText !== null && text !== lastGeneratedText;

  // Latest text/last-generated, read inside the debounced effect without making
  // it a dependency (which would restart the timer on every keystroke)
  const textRef = useRef(text);
  const lastGeneratedRef = useRef(lastGeneratedText);
  textRef.current = text;
  lastGeneratedRef.current = lastGeneratedText;

  // Keep the plain-text part in sync with the HTML without a button press.
  // Skips once the user has hand-edited the text -- the button is the explicit
  // override for that case.
  useEffect(() => {
    const untouched =
      !textRef.current.trim() || textRef.current === lastGeneratedRef.current;
    if (!untouched) return;

    // HTML cleared -> clear the auto-generated text too, so they never drift
    if (isHtmlEmpty(html)) {
      if (textRef.current) {
        onTextChange("");
        setLastGeneratedText("");
      }
      return;
    }

    const timer = setTimeout(() => {
      const generated = htmlToPlainText(html);
      if (generated !== textRef.current) {
        onTextChange(generated);
        setLastGeneratedText(generated);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [html, onTextChange]);



  const handleFormat = () => {
    if (htmlEmpty) return;
    handleHtmlChange(formatHtml(html));
    toast.success("HTML formatted", { duration: 2000 });
  };

  const handleCleanStyles = () => {
    if (htmlEmpty) return;
    const cleaned = cleanPastedStyles(html);
    if (cleaned === html) {
      toast.info("Nothing to clean", {
        description: "No leftover clipboard styles found",
      });
      return;
    }
    handleHtmlChange(cleaned);
    toast.success("Clipboard styles removed", {
      duration: 3000,
      description: "Real styling was left untouched",
    });
  };

  return (
    <div
      className={cn(
        "rounded-lg border border-gray-300 bg-white overflow-hidden",
        className
      )}
    >
      <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-2">
        <div className="flex items-center gap-1 py-1.5">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isEmptyTab =
              (tab.id !== "text" && htmlEmpty) ||
              (tab.id === "text" && !text.trim());

            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  "flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors cursor-pointer",
                  activeTab === tab.id
                    ? "bg-white text-slate-900 shadow-sm border border-gray-200"
                    : "text-slate-500 hover:bg-white/60"
                )}
              >
                <Icon className="h-3.5 w-3.5" />
                {tab.label}
                {isEmptyTab && (
                  <span className="text-[10px] text-slate-400">empty</span>
                )}
              </button>
            );
          })}
        </div>

        <div className="mr-1 flex items-center gap-1">
          {activeTab === "html" && (
            <>
              <button
                type="button"
                onClick={handleFormat}
                disabled={htmlEmpty}
                className={cn(
                  "flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors",
                  htmlEmpty
                    ? "text-slate-300 cursor-not-allowed"
                    : "text-slate-600 hover:bg-slate-200/70 cursor-pointer"
                )}
                title="Indent the markup so it is readable"
              >
                <Indent className="h-3.5 w-3.5" />
                Format
              </button>
              <button
                type="button"
                onClick={handleCleanStyles}
                disabled={htmlEmpty}
                className={cn(
                  "flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors",
                  htmlEmpty
                    ? "text-slate-300 cursor-not-allowed"
                    : "text-slate-600 hover:bg-slate-200/70 cursor-pointer"
                )}
                title="Strip the styles your browser added when copying"
              >
                <Brush className="h-3.5 w-3.5" />
                Clean styles
              </button>
              <span className="mx-1 h-4 w-px bg-gray-200" />
            </>
          )}
          
        </div>
      </div>

      {activeTab === "design" && (
        <>
          {isFullHtmlDocument(html) && (
            <p className="border-b border-amber-100 bg-amber-50 px-4 py-1.5 text-xs text-amber-700">
              Full HTML email — Design flattens its table layout to make it
              editable. The saved markup stays exactly as uploaded unless you
              edit it here.
            </p>
          )}
          <Suspense
            fallback={
              <div
                style={{ height: minHeight }}
                className="flex items-center justify-center gap-2 text-sm text-slate-400"
              >
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading editor…
              </div>
            }
          >
            <RichTextEditor
              value={html}
              onChange={handleHtmlChange}
              minHeight={minHeight}
              placeholder="Paste your content here — rich content or raw HTML both work — or start typing."
              className="!border-0 !rounded-none"
            />
          </Suspense>
        </>
      )}

      {activeTab === "preview" && (
        <div style={{ height: minHeight }} className="bg-gray-50">
          {htmlEmpty ? (
            <div className="flex h-full items-center justify-center text-sm text-slate-400">
              Nothing to preview yet.
            </div>
          ) : (
            // Iframe so the email's own <style> applies to it and cannot
            // leak into this page
            <iframe
              srcDoc={html}
              className="h-full w-full border-0 bg-white"
              sandbox="allow-same-origin"
              title="Content preview"
            />
          )}
        </div>
      )}

      {activeTab === "html" && (
        <div style={{ height: minHeight }}>
          <textarea
            value={displayedHtml}
            onChange={(e) => handleHtmlChange(e.target.value)}
            spellCheck={false}
            placeholder="Paste or edit the raw HTML here."
            className="h-full w-full resize-none p-4 font-mono text-[13px] leading-relaxed text-slate-800 placeholder-slate-400 focus:outline-none"
            style={{ tabSize: 2 }}
          />
        </div>
      )}

      {activeTab === "text" && (
        <div style={{ height: minHeight }} className="flex flex-col">
          {textEditedByHand && (
            <p className="border-b border-amber-100 bg-amber-50 px-4 py-1.5 text-xs text-amber-700">
              Edited by hand — regenerating will overwrite these changes.
            </p>
          )}
          <textarea
            value={text}
            onChange={(e) => onTextChange(e.target.value)}
            spellCheck={false}
            placeholder="The plain-text version shown by email clients that can't render HTML. Write it here, or generate it from the HTML above."
            className="w-full flex-1 resize-none p-4 font-mono text-[13px] leading-relaxed text-slate-800 placeholder-slate-400 focus:outline-none"
          />
        </div>
      )}
    </div>
  );
}
